import { useEffect } from 'react';

import { useMap } from 'react-leaflet';
import L from 'leaflet';
import type { MutableRefObject } from 'react';
import { lv95ToWgs84ForOneCoordinate } from './coordinateTransforms';
import type { GridParams } from 'contexts/GridContext';
import { firstPolygon, normalizeMultiPolygon } from 'utils/multiPolygonHelper';

interface Lv95Coordinate {
  x: number;
  y: number;
}
type PolygonRef = MutableRefObject<L.Polygon | null>;

interface PolygonStyle {
  legalBorder: L.PathOptions;
  buildableBorder: L.PathOptions;
  analysisArea: L.PathOptions;
}

interface RenderIfThereIsAPolygonProps {
  gridParams: GridParams;
  Style: PolygonStyle;
  legalBorderRef: PolygonRef;
  buildableBorderRef: PolygonRef;
  analysisAreaRef: PolygonRef;
  legalBorderInactiveRefs?: MutableRefObject<L.Polygon[]>;
  buildableBorderInactiveRefs?: MutableRefObject<L.Polygon[]>;
  analysisAreaInactiveRefs?: MutableRefObject<L.Polygon[]>;
  activeAnalysisAreaIndex?: number;
  showInactiveNonAnalysisBorders?: boolean;
  showOnlyAnalysisAreas?: boolean;
  showInactiveAnalysisAreas?: boolean;
  inactiveLegalBorderStyle?: L.PathOptions;
  inactiveBuildableBorderStyle?: L.PathOptions;
  inactiveAnalysisAreaStyle?: L.PathOptions;
}

const RenderIfThereIsAPolygon = ({
  gridParams,
  Style,
  legalBorderRef,
  buildableBorderRef,
  analysisAreaRef,
  legalBorderInactiveRefs,
  buildableBorderInactiveRefs,
  analysisAreaInactiveRefs,
  activeAnalysisAreaIndex,
  showInactiveNonAnalysisBorders = false,
  showOnlyAnalysisAreas = false,
  showInactiveAnalysisAreas = true,
  inactiveLegalBorderStyle,
  inactiveBuildableBorderStyle,
  inactiveAnalysisAreaStyle,
}: RenderIfThereIsAPolygonProps) => {
  const map = useMap();

  const toWgs84 = (coordinates: Lv95Coordinate[] = []) =>
    coordinates.map((coordinate) => lv95ToWgs84ForOneCoordinate(coordinate.x, coordinate.y));

  const normalizePolygonForComparison = (coordinates: Lv95Coordinate[] = []) => {
    if (coordinates.length < 2) {
      return coordinates;
    }

    const firstPoint = coordinates[0];
    const lastPoint = coordinates[coordinates.length - 1];
    const isClosed = firstPoint?.x === lastPoint?.x && firstPoint?.y === lastPoint?.y;

    return isClosed ? coordinates.slice(0, -1) : coordinates;
  };

  const arePolygonsEqual = (first: Lv95Coordinate[] = [], second: Lv95Coordinate[] = []) => {
    const normalizedFirst = normalizePolygonForComparison(first);
    const normalizedSecond = normalizePolygonForComparison(second);

    return (
      normalizedFirst.length === normalizedSecond.length &&
      normalizedFirst.every(
        (coordinate, index) =>
          coordinate.x === normalizedSecond[index]?.x && coordinate.y === normalizedSecond[index]?.y
      )
    );
  };

  const syncPolygon = (
    coordinates: Lv95Coordinate[] | null | undefined,
    style: L.PathOptions,
    polygonRef: PolygonRef
  ) => {
    if (!map) {
      return;
    }

    const normalizedCoordinates = firstPolygon<Lv95Coordinate>(coordinates);

    if (!normalizedCoordinates.length) {
      if (polygonRef.current) {
        polygonRef.current.remove();
        polygonRef.current = null;
      }
      return;
    }

    const wgs84Coordinates = toWgs84(normalizedCoordinates);

    if (!polygonRef.current) {
      polygonRef.current = new L.Polygon(wgs84Coordinates, style);
      polygonRef.current.addTo(map);
      return;
    }

    polygonRef.current.setStyle(style);
    polygonRef.current.setLatLngs(wgs84Coordinates);

    if (!map.hasLayer(polygonRef.current)) {
      polygonRef.current.addTo(map);
    }
  };

  const resolvedActivePolygonIndex = (coordinates: Lv95Coordinate[][], activeCoordinates: Lv95Coordinate[] = []) => {
    return (
      activeAnalysisAreaIndex ??
      coordinates.findIndex((polygonCoordinates) => arePolygonsEqual(polygonCoordinates, activeCoordinates))
    );
  };

  const syncInactivePolygons = ({
    coordinates,
    activeCoordinates,
    inactiveRefs,
    baseStyle,
    fallbackStyle,
  }: {
    coordinates: Lv95Coordinate[][];
    activeCoordinates: Lv95Coordinate[];
    inactiveRefs?: MutableRefObject<L.Polygon[]>;
    baseStyle: L.PathOptions;
    fallbackStyle: L.PathOptions;
  }) => {
    if (!map) {
      return;
    }

    const resolvedActiveIndex = resolvedActivePolygonIndex(coordinates, activeCoordinates);
    const inactivePolygons =
      resolvedActiveIndex >= 0 ? coordinates.filter((_, index) => index !== resolvedActiveIndex) : coordinates;

    inactiveRefs?.current?.forEach((layer) => layer.remove());
    const inactiveLayers = inactivePolygons.map((polygonCoordinates) => {
      const layer = new L.Polygon(toWgs84(polygonCoordinates), {
        ...baseStyle,
        ...fallbackStyle,
      });
      layer.addTo(map);
      return layer;
    });

    if (inactiveRefs) {
      inactiveRefs.current = inactiveLayers;
    }
  };

  useEffect(() => {
    const legalBorders = normalizeMultiPolygon<Lv95Coordinate>(
      gridParams.legalBorders?.length ? gridParams.legalBorders : gridParams.legalBorder
    );
    const buildableBorders = normalizeMultiPolygon<Lv95Coordinate>(
      gridParams.buildableBorders?.length ? gridParams.buildableBorders : gridParams.buildableBorder
    );
    const analysisAreas = normalizeMultiPolygon<Lv95Coordinate>(
      gridParams.analysisAreas?.length ? gridParams.analysisAreas : gridParams.analysisArea
    );

    if (showOnlyAnalysisAreas) {
      syncPolygon([], Style.legalBorder, legalBorderRef);
      syncPolygon([], Style.buildableBorder, buildableBorderRef);
    } else {
      syncPolygon(gridParams.legalBorder, Style.legalBorder, legalBorderRef);
      syncPolygon(gridParams.buildableBorder, Style.buildableBorder, buildableBorderRef);
    }
    syncPolygon(gridParams.analysisArea, Style.analysisArea, analysisAreaRef);
    if (!showOnlyAnalysisAreas && showInactiveNonAnalysisBorders) {
      syncInactivePolygons({
        coordinates: legalBorders,
        activeCoordinates: gridParams.legalBorder,
        inactiveRefs: legalBorderInactiveRefs,
        baseStyle: Style.legalBorder,
        fallbackStyle: {
          color: '#c084fc',
          fillColor: '#c084fc',
          fillOpacity: 0.14,
        },
      });
      syncInactivePolygons({
        coordinates: buildableBorders,
        activeCoordinates: gridParams.buildableBorder,
        inactiveRefs: buildableBorderInactiveRefs,
        baseStyle: Style.buildableBorder,
        fallbackStyle: {
          color: '#fdba74',
          fillColor: '#fdba74',
          fillOpacity: 0.14,
        },
      });
    } else {
      legalBorderInactiveRefs?.current?.forEach((layer) => layer.remove());
      if (legalBorderInactiveRefs) {
        legalBorderInactiveRefs.current = [];
      }
      buildableBorderInactiveRefs?.current?.forEach((layer) => layer.remove());
      if (buildableBorderInactiveRefs) {
        buildableBorderInactiveRefs.current = [];
      }
    }
    if (showInactiveAnalysisAreas) {
      syncInactivePolygons({
        coordinates: analysisAreas,
        activeCoordinates: gridParams.analysisArea,
        inactiveRefs: analysisAreaInactiveRefs,
        baseStyle: Style.analysisArea,
        fallbackStyle: {
          color: '#9ca3af',
          fillColor: '#9ca3af',
          fillOpacity: 0.2,
          ...inactiveAnalysisAreaStyle,
        },
      });
    } else {
      analysisAreaInactiveRefs?.current?.forEach((layer) => layer.remove());
      if (analysisAreaInactiveRefs) {
        analysisAreaInactiveRefs.current = [];
      }
    }
  }, [
    map,
    gridParams.legalBorders,
    gridParams.legalBorder,
    gridParams.buildableBorders,
    gridParams.buildableBorder,
    gridParams.analysisArea,
    gridParams.analysisAreas,
    Style.legalBorder,
    Style.buildableBorder,
    Style.analysisArea,
    legalBorderRef,
    buildableBorderRef,
    analysisAreaRef,
    legalBorderInactiveRefs,
    buildableBorderInactiveRefs,
    analysisAreaInactiveRefs,
    activeAnalysisAreaIndex,
    showInactiveNonAnalysisBorders,
    showOnlyAnalysisAreas,
    showInactiveAnalysisAreas,
    gridParams.analysisArea,
    inactiveLegalBorderStyle,
    inactiveBuildableBorderStyle,
    inactiveAnalysisAreaStyle,
  ]);

  useEffect(() => {
    return () => {
      if (legalBorderRef?.current) {
        legalBorderRef.current.remove();
        legalBorderRef.current = null;
      }
      if (buildableBorderRef?.current) {
        buildableBorderRef.current.remove();
        buildableBorderRef.current = null;
      }
      if (analysisAreaRef.current) {
        analysisAreaRef.current.remove();
        analysisAreaRef.current = null;
      }
      legalBorderInactiveRefs?.current?.forEach((layer) => layer.remove());
      if (legalBorderInactiveRefs) {
        legalBorderInactiveRefs.current = [];
      }
      buildableBorderInactiveRefs?.current?.forEach((layer) => layer.remove());
      if (buildableBorderInactiveRefs) {
        buildableBorderInactiveRefs.current = [];
      }
      analysisAreaInactiveRefs?.current?.forEach((layer) => layer.remove());
      if (analysisAreaInactiveRefs) {
        analysisAreaInactiveRefs.current = [];
      }
    };
  }, [
    legalBorderRef,
    buildableBorderRef,
    analysisAreaRef,
    legalBorderInactiveRefs,
    buildableBorderInactiveRefs,
    analysisAreaInactiveRefs,
  ]);

  // Cancel drawing operation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (!map?.pm) {
          return;
        }
        map.pm.disableDraw('Rectangle');
        map.pm.disableDraw('Polygon');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [map]);

  return null;
};

export default RenderIfThereIsAPolygon;
